import { dispatchEvent } from '../../../utils/events.js';

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host{
    display:inline-block;
    box-sizing: border-box;
    font-family: var(--ctl-font-sans);
  }

  .trigger{
    display:inline-block;
  }

  .panel{
    position: fixed;
    z-index: var(--ctl-z-dropdown);
    min-width: var(--ctl-dropdown-min-width);
    max-width: var(--ctl-dropdown-max-width);
    background: var(--ctl-color-surface);
    border: var(--ctl-border-width-thin) solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-lg);
    box-shadow: var(--ctl-shadow-md);
    padding: var(--ctl-space-1);
    opacity: 0;
    transform: translateY(calc(-1 * var(--ctl-space-1)));
    pointer-events: none;
    transition: opacity var(--ctl-transition-fast), transform var(--ctl-transition-fast);
  }

  :host([open]) .panel{
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
  }

  /* for focus outlines inside */
  ::slotted(*:focus-visible){
    outline: none;
    box-shadow: var(--ctl-focus-ring);
    border-radius: var(--ctl-radius-md);
  }
</style>

<span class="trigger" part="trigger">
  <slot name="trigger"></slot>
</span>

<div class="panel" part="panel" id="panel" role="dialog" aria-modal="false">
  <slot name="content"></slot>
</div>
`;

function clamp(n, a, b){ return Math.max(a, Math.min(b, n)); }

export class CtlDropdown extends HTMLElement {
  static get observedAttributes(){
    return ["open", "placement", "offset", "label"];
  }

  constructor(){
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));

    this._panel = this.shadowRoot.getElementById("panel");
    this._onDocPointerDown = this._onDocPointerDown.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onTriggerClick = this._onTriggerClick.bind(this);
    this._onSlotChange = this._onSlotChange.bind(this);
    this._listenersAttached = false;
    
    // Generate unique ID for trigger accessibility
    this._triggerId = `dropdown-trigger-${Math.random().toString(36).substr(2, 9)}`;
  }

  connectedCallback(){
    if (!this.hasAttribute("placement")) this.setAttribute("placement", "bottom-start");
    if (!this.hasAttribute("offset")) this.setAttribute("offset", "8");

    const triggerSlot = this.shadowRoot.querySelector('slot[name="trigger"]');
    this._triggerSlot = triggerSlot;
    triggerSlot.addEventListener("slotchange", this._onSlotChange);
    this._wireTrigger();
    this._setupAccessibility();
    this._sync();
  }

  disconnectedCallback(){
    document.removeEventListener("pointerdown", this._onDocPointerDown, true);
    document.removeEventListener("keydown", this._onKeyDown, true);
    this._listenersAttached = false;
    if (this._triggerSlot) {
      this._triggerSlot.removeEventListener("slotchange", this._onSlotChange);
    }
    this._unwireTrigger();
  }

  attributeChangedCallback(name, oldValue, newValue){
    if (oldValue === newValue) return;
    
    if (name === "label" && this._panel) {
      this._setupAccessibility();
    }
    
    this._sync();
  }

  get open(){ return this.hasAttribute("open"); }
  set open(v){ v ? this.setAttribute("open","") : this.removeAttribute("open"); }

  get placement(){ return this.getAttribute("placement") || "bottom-start"; }
  set placement(v){ this.setAttribute("placement", String(v || "bottom-start")); }

  get offset(){ return Number(this.getAttribute("offset") || "8") || 8; }
  set offset(v){ this.setAttribute("offset", String(Number(v) || 8)); }

  toggle(force){
    const next = (typeof force === "boolean") ? force : !this.open;
    this.open = next;
  }

  show(){
    if (this.open) return;
    this.open = true;
  }

  hide(reason = "dismiss"){
    if (!this.open) return;
    this._closeReason = reason;
    this.open = false;
  }

  _sync(){
    if (this._triggerEl) {
      this._triggerEl.setAttribute("aria-expanded", this.open ? "true" : "false");
    }

    if (this.open){
      this._position();
      if (!this._listenersAttached) {
        document.addEventListener("pointerdown", this._onDocPointerDown, true);
        document.addEventListener("keydown", this._onKeyDown, true);
        this._listenersAttached = true;
      }

      dispatchEvent(this, "ctl-open-change", { open: true });

      // focus first focusable inside content (menu will handle roving focus too)
      queueMicrotask(() => this._focusFirst());
    } else {
      document.removeEventListener("pointerdown", this._onDocPointerDown, true);
      document.removeEventListener("keydown", this._onKeyDown, true);
      this._listenersAttached = false;

      dispatchEvent(this, "ctl-open-change", { open: false, reason: this._closeReason || "dismiss" });

      this._closeReason = null;
    }
  }
  
  _setupAccessibility(){
    const trigger = this._getTriggerEl();
    const label = this.getAttribute("label");
    
    // Strategy: Use aria-labelledby if trigger has ID, otherwise use aria-label
    if (trigger) {
      // Ensure trigger has an ID
      if (!trigger.id) {
        trigger.id = this._triggerId;
      }
      this._panel.setAttribute("aria-labelledby", trigger.id);
      // Remove aria-label if set
      this._panel.removeAttribute("aria-label");
    } else if (label) {
      // No trigger found, use explicit label attribute
      this._panel.setAttribute("aria-label", label);
      this._panel.removeAttribute("aria-labelledby");
    } else {
      // Fallback to generic label
      this._panel.setAttribute("aria-label", "Dropdown menu");
      this._panel.removeAttribute("aria-labelledby");
    }
  }

  _onSlotChange(){
    this._unwireTrigger();
    this._wireTrigger();
  }

  _getTriggerEl(){
    const slot = this.shadowRoot.querySelector('slot[name="trigger"]');
    const assigned = slot.assignedElements({ flatten: true }) || [];
    return assigned[0] || null;
  }

  _wireTrigger(){
    const el = this._getTriggerEl();
    if (!el) return;
    this._triggerEl = el;
    el.addEventListener("click", this._onTriggerClick);
    el.setAttribute("aria-haspopup", "menu");
    el.setAttribute("aria-expanded", this.open ? "true" : "false");
    
    // Update accessibility labeling when trigger is wired
    this._setupAccessibility();
  }

  _unwireTrigger(){
    if (!this._triggerEl) return;
    this._triggerEl.removeEventListener("click", this._onTriggerClick);
    this._triggerEl = null;
  }

  _onTriggerClick(e){
    e.preventDefault();
    this.toggle();
  }

  _onDocPointerDown(e){
    if (!this.open) return;
    const path = e.composedPath ? e.composedPath() : [];
    const inside = path.includes(this) || path.includes(this._panel);
    if (inside) return;
    // If click is on trigger, allow trigger handler to toggle
    if (this._triggerEl && path.includes(this._triggerEl)) return;
    this.hide("outside");
  }

  _onKeyDown(e){
    if (!this.open) return;
    if (e.key === "Escape"){
      e.preventDefault();
      this.hide("escape");
      this._triggerEl?.focus?.();
    }
  }

  _position(){
    const trigger = this._getTriggerEl();
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const panel = this._panel;

    // Ensure measurable
    panel.style.left = "-9999px";
    panel.style.top = "-9999px";

    const offset = this.offset;
    const placement = this.placement;

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // measure panel
    const pr = panel.getBoundingClientRect();
    const pw = pr.width || 220;
    const ph = pr.height || 160;

    let left = rect.left;
    let top = rect.bottom + offset;

    if (placement.startsWith("top")){
      top = rect.top - ph - offset;
    }

    // start/end alignment
    if (placement.endsWith("end")){
      left = rect.right - pw;
    } else {
      left = rect.left;
    }

    // clamp to viewport with small margin
    const margin = 8;
    left = clamp(left, margin, vw - pw - margin);
    top = clamp(top, margin, vh - ph - margin);

    panel.style.left = `${Math.round(left)}px`;
    panel.style.top = `${Math.round(top)}px`;
  }

  _focusFirst(){
    const contentSlot = this.shadowRoot.querySelector('slot[name="content"]');
    const nodes = contentSlot.assignedElements({ flatten: true }) || [];
    const root = nodes[0] || this._panel;

    const focusable = root.querySelector?.(
      '[tabindex]:not([tabindex="-1"]),button,a,input,select,textarea,[role="menuitem"]'
    );

    if (focusable && typeof focusable.focus === "function") {
      focusable.focus();
    }
  }
}

customElements.define("ctl-dropdown", CtlDropdown);
